import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Coins, TrendingUp, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../ludo/ludo.css'; // shared shell (app-container, etc.)
import { audioEngine } from './audio';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import Icon from '@baaligames/shared-ui/components/Icon';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';

// 2026-05-30 — Plinko top-bar parity. Other games (Ludo, Snake & Ladder,
// Chess, etc.) render a standardized TopBar: two circular buttons on the
// left (back, settings), and a wallet chip on the right. Settings opens
// the shared GameSettingsMenu (which holds the sound toggle). Plinko was
// the odd one out — a Lucide-icon back, an inline mute toggle, a solid
// flat wallet chip with a hand-drawn "₹" coin — so it stuck out next to
// the other tiles. This file imports the same `Icon` + `GameSettingsMenu`
// the other games use, with Plinko-themed gradient colors (purple →
// fuchsia) instead of Ludo-purple or SL-green.

// Plinko-specific asset paths — match the directory layout other games
// use (e.g. /games/snake_ladder/Settings.png). Files already exist in
// player/public/games/plinko/ from the original asset drop.
const IMG = {
  settings: '/games/plinko/Settings.png',
  coin:     '/games/plinko/Coin.png',
  plus:     '/games/plinko/Plus.png',
} as const;

function fmt(n: number) { return Math.floor(n).toLocaleString('en-IN'); }

// Backend-authoritative shape — every drop's slot, multiplier, payout
// and animation PATH are decided on the server (shared/src/games/plinko/
// engine.js) so no one can hack a payout client-side. The frontend's
// only job is to animate what the server already decided.
type PlayResponse = {
  sessionId: number;
  slot: number;
  multiplier: number;
  payout: number;
  path: Array<'L' | 'R'>;       // length === rowCount
  // `surpriseWin` was removed from this response on 2026-09-06 with the
  // surprise-win override (plan docs/ADRENALINE_SHARED_TABLES_PLAN.md §1.4).
  // Nothing in this file ever read it.
  result: 'win' | 'loss' | 'wash';
  newBalance: number;
};

// Types for our Physics Engine
interface HistoryItem {
  id: string;
  mult: number;
  isBigWin: boolean;
}
interface Ball {
  id: string;
  x: number;
  y: number;
  row: number; // -1 to rows
  col: number; // current col
  path: number[]; // precalculated column path
  progress: number; // 0 to 1
  value: number; // bet amount (used for ball colour + display)
  // 2026-05-30 — server-authoritative payout/multiplier. The ball is
  // marked with these the moment /play returns, but they're only APPLIED
  // (added to wallet + Session Winnings) when the ball physically lands.
  // This prevents the v1 double-credit bug where the FE credited on
  // /play response AND credited again on landing (using a FE-local
  // multipliers table that could drift from the server). Now there is
  // exactly ONE credit, at landing, with the server's actual numbers.
  payout: number;     // ₹ to credit when this ball lands
  multiplier: number; // shown in floating text + history strip
}

interface FloatingText {
  id: string;
  text: string;
  x: number;
  y: number;
  createdAt: number;
}

export default function PlinkoPage() {
  const [betAmount, setBetAmount] = useState<number>(10);
  // Ball-value chips are admin-configurable (plinko_config.bet_presets). Default
  // mirrors the seeded config; the /me/plinko/config fetch below overrides it so
  // changing presets never needs a code change. Any count renders (adaptive grid).
  // NOTE: every value must satisfy bet × (top multiplier 1000× on High/16) ≤
  // max_payout_per_drop (50000) or the drop is REFUSED — so the ceiling is ₹50.
  const [betPresets, setBetPresets] = useState<number[]>([10, 20, 30, 50]);
  // Default to the gentler Medium/12 board (mirrors plinko_config.default_risk /
  // default_row_count; the /me/plinko/config fetch below re-syncs to the live admin
  // value). Was hardcoded High/16 — the harshest board (mostly 0.2× = lose 80%) —
  // which ignored the admin default and gave every new player a punishing first
  // impression. Same house edge at any risk; Medium is far happier. (2026-08-07 walk.)
  const [risk, setRisk] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [rows, setRows] = useState<number>(12);

  // History & Hits
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [bucketHits, setBucketHits] = useState<number[]>(new Array(17).fill(0));
  const [isMuted, setIsMuted] = useState(false);
  
  // Player Session State
  const [totalWonAmt, setTotalWonAmt] = useState<number>(0);
  const [totalBetAmt, setTotalBetAmt] = useState<number>(0);
  const [walletAmt, setWalletAmt] = useState<number>(0); // seeded from /api/me/plinko/config
  const playInFlightRef = useRef<boolean>(false); // prevent overlapping API calls
  const [playError, setPlayError] = useState<string | null>(null);
  // 2026-05-30 — shared settings panel state. Holds the sound toggle now
  // that it's no longer a standalone button in the top bar.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  
  const [animateWinnings, setAnimateWinnings] = useState(false);
  const prevWinningsRef = useRef(0);
  const autoBetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (totalWonAmt > prevWinningsRef.current) {
      setAnimateWinnings(true);
      const timer = setTimeout(() => setAnimateWinnings(false), 400);
      prevWinningsRef.current = totalWonAmt;
      return () => clearTimeout(timer);
    }
  }, [totalWonAmt]);

  useEffect(() => {
    // Cleanup auto-bet interval on unmount
    return () => {
      if (autoBetTimerRef.current) {
        clearInterval(autoBetTimerRef.current);
      }
    };
  }, []);

  // 2026-05-30 — seed real wallet balance + server bet limits.
  // Runs once on mount; the server is the source of truth for every
  // tunable here (admin can change house edge / presets / multipliers
  // and a refresh picks them up). We don't gate the page on this —
  // failure just leaves the user with 0 balance until login refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.get<{
          enabled: boolean;
          maintenanceMessage?: string;
          balance: number;
          bet: { presets: number[]; min: number; max: number };
          board?: { defaultRowCount?: number; defaultRisk?: string };
        }>('/me/plinko/config');
        if (!cancelled) {
          setWalletAmt(Number(cfg.balance) || 0);
          if (cfg.bet?.min && betAmount < cfg.bet.min) setBetAmount(cfg.bet.min);
          if (cfg.bet?.presets?.length) setBetPresets(cfg.bet.presets);
          // 2026-08-07: honour the admin's configured default risk + row count
          // (plinko_config.default_risk / default_row_count) — these were exposed
          // by the backend but the FE ignored them, hardcoding the brutal High/16.
          // A new player now opens on the gentle admin default (medium/12).
          const dr = cfg.board?.defaultRisk;
          if (dr === 'low' || dr === 'medium' || dr === 'high') {
            setRisk((dr.charAt(0).toUpperCase() + dr.slice(1)) as 'Low' | 'Medium' | 'High');
          }
          if (cfg.board?.defaultRowCount && [8, 12, 16].includes(cfg.board.defaultRowCount)) {
            setRows(cfg.board.defaultRowCount);
          }
          // 2026-05-30 (migration 105) — if admin has flipped enabled=false
          // (scheduled maintenance, emergency stop), surface the admin-
          // configured maintenance message right away so the user
          // doesn't have to click PLAY to learn the game is off.
          if (cfg.enabled === false && cfg.maintenanceMessage) {
            setPlayError(cfg.maintenanceMessage);
          }
        }
      } catch { /* leave defaults; the user will see "Not enough balance" if they try to play */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-clear the play error after 4s so it doesn't linger.
  useEffect(() => {
    if (!playError) return;
    const id = setTimeout(() => setPlayError(null), 4000);
    return () => clearTimeout(id);
  }, [playError]);

  // 2026-05-30 — close the Plinko play session when the user leaves
  // the page, so the bet/win ledger entries land immediately instead of
  // waiting for the 2-min idle timeout. Two triggers:
  //   1. component unmount (SPA route change away)
  //   2. beforeunload (tab close / hard navigation)
  // The backend is idempotent — calling /session/end with no active
  // session is a no-op, so duplicates are safe. We use the api helper
  // (auto-attaches the bearer token) for unmount; beforeunload uses
  // sendBeacon-style keepalive fetch which survives tear-down.
  useEffect(() => {
    const onBeforeUnload = () => {
      try {
        // keepalive lets the request finish after the page is gone
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon?.('/api/me/plinko/session/end', blob);
      } catch { /* best-effort */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      // SPA route-away: use the api helper so auth headers are attached.
      api.post('/me/plinko/session/end', {}).catch(() => { /* best-effort */ });
    };
  }, []);
  
  // Play Button Combo State
  const [playClickCount, setPlayClickCount] = useState<number>(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout>();
  const historyTimeoutRef = useRef<NodeJS.Timeout>();
  const shakeTimeoutRef = useRef<NodeJS.Timeout>();

  const [screenShake, setScreenShake] = useState(false);
  
  // Physics States
  const [balls, setBalls] = useState<Ball[]>([]);
  const [activePins, setActivePins] = useState<Record<string, number>>({});
  const [activeBuckets, setActiveBuckets] = useState<Record<number, number>>({});
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);

  // Refs for animation loop (to avoid closure stale state)
  const ballsRef = useRef<Ball[]>([]);
  const activePinsRef = useRef<Record<string, number>>({});
  const activeBucketsRef = useRef<Record<number, number>>({});
  const floatingTextsRef = useRef<FloatingText[]>([]);
  const lastTimeRef = useRef<number>(0);
  const frameRef = useRef<number>();

  // Toggle Sound
  const handleToggleSound = () => {
    audioEngine.init();
    audioEngine.toggle(isMuted);
    setIsMuted(!isMuted);
  };

  // Sync refs with state when we need to (only for reading in the loop if needed, 
  // but better yet, we just do functional state updates).
  // Actually, keeping the entire simulation in a ref and syncing to state is much safer.
  
  const getMultipliers = useCallback((r: number, rsk: string) => {
    // Exact ~99% RTP Multiplier Arrays matching Stake.com
    // Only 8, 12, and 16 rows are permitted by the UI.
    const MULTIPLIERS: Record<number, Record<string, number[]>> = {
      8: {
        Low: [0.5, 1, 1.1, 2, 5.6],
        Medium: [0.4, 0.7, 2, 3, 13],
        High: [0.2, 0.3, 1.5, 8.5, 29]
      },
      12: {
        Low: [0.5, 1, 1.1, 1.1, 1.4, 4, 10],
        Medium: [0.3, 0.5, 1.1, 1.5, 3, 11, 33],
        High: [0.2, 0.2, 0.9, 2, 8, 24, 170]
      },
      16: {
        Low: [0.5, 1, 1.1, 1.2, 1.4, 1.4, 2, 9, 16],
        Medium: [0.3, 0.5, 1, 1.5, 3, 5, 10, 41, 110],
        High: [0.2, 0.2, 0.2, 2, 4, 9, 26, 130, 1000]
      }
    };

    const rowMap = MULTIPLIERS[r] || MULTIPLIERS[16];
    const halfArray = rowMap[rsk] || rowMap.High;
    
    // The halfArray only defines the center to the right edge (index 0 is center).
    // We mirror it for the full bucket array (size r + 1).
    const buckets = r + 1;
    return Array.from({ length: buckets }, (_, i) => {
      const dist = Math.round(Math.abs(i - r / 2));
      return halfArray[dist];
    });
  }, []);

  const multipliers = useMemo(() => getMultipliers(rows, risk), [rows, risk, getMultipliers]);

  // Handle rows change (reset hits)
  useEffect(() => {
    setBucketHits(new Array(rows + 1).fill(0));
    setBalls([]);
    ballsRef.current = [];
  }, [rows, risk]);

  // Math for Pin Coordinates
  const getPinCoords = useCallback((r: number, c: number, totalRows: number) => {
    const dx = 100 / (totalRows + 2);
    const dy = 85 / totalRows;

    if (r === -1) {
      // Spawn point (above row 0)
      const pegsInRow = 3;
      const rowWidth = (pegsInRow - 1) * dx;
      const leftOffset = (100 - rowWidth) / 2;
      return { x: leftOffset + c * dx, y: -5 };
    }
    if (r === totalRows) {
      // Bucket target
      // c is the path column (1 to totalRows + 1). Bucket index is c - 1.
      const bucketIdx = c - 1;
      const bottomPegsInRow = totalRows + 2;
      const bottomRowWidth = (bottomPegsInRow - 1) * dx;
      const bottomLeftOffset = (100 - bottomRowWidth) / 2;
      return {
        x: bottomLeftOffset + (bucketIdx + 0.5) * dx,
        y: 95
      };
    }
    
    // Normal pin
    const pegsInRow = r + 3;
    const rowWidth = (pegsInRow - 1) * dx;
    const leftOffset = (100 - rowWidth) / 2;
    return {
      x: leftOffset + c * dx,
      y: (r + 0.5) * dy
    };
  }, []);

  // Animation Loop
  const animate = useCallback((time: number) => {
    if (!lastTimeRef.current) lastTimeRef.current = time;
    const dt = time - lastTimeRef.current;
    lastTimeRef.current = time;

    // Simulation Speed
    const speed = 0.003 * dt;

    let stateChanged = false;

    // 1. Update Balls
    const currentBalls = [...ballsRef.current];
    const newBalls: Ball[] = [];
    const now = Date.now();

    for (const ball of currentBalls) {
      ball.progress += speed;
      
      let startCoords = getPinCoords(ball.row, ball.col, rows);
      let endCoords = getPinCoords(ball.row + 1, ball.path[ball.row + 2], rows);

      if (ball.progress >= 1) {
        // Hit a pin or bucket!
        ball.row += 1;
        ball.col = ball.path[ball.row + 1];
        ball.progress -= 1;
        stateChanged = true;

        if (ball.row < rows) {
          // Hit a pin
          activePinsRef.current[`${ball.row}-${ball.col}`] = now;
          audioEngine.playBounce();
        } else {
          // Hit the final bucket!
          const bucketIndex = ball.col - 1;
          // 2026-05-30 — server-authoritative. Use ball.payout & ball.multiplier
          // (stamped from /play response), NOT a recompute against the FE-local
          // `multipliers` array. The FE array can drift from the server's
          // (admin can change the server table without rebuilding the FE bundle).
          // The server already decided how much to pay; we only animate to that.
          const mult = ball.multiplier;
          const winAmount = ball.payout;

          activeBucketsRef.current[bucketIndex] = now;
          audioEngine.playWin(mult);

          if (mult >= 9) {
            setScreenShake(true);
            if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
            shakeTimeoutRef.current = setTimeout(() => setScreenShake(false), 500);
          }

          // Add to bucket hits state directly (React queue)
          setBucketHits(prev => {
            const next = [...prev];
            if (bucketIndex >= 0 && bucketIndex < next.length) {
              next[bucketIndex] += 1;
            }
            return next;
          });

          // 2026-05-30 — credit BOTH wallet and Session Winnings exactly here,
          // when the ball lands. handlePlay() above only deducted the bet
          // (so the user immediately sees ₹-bet) and stamped the payout on the
          // ball; the credit lives here so the win visibly arrives WITH the ball.
          if (winAmount > 0) {
            setTotalWonAmt(prev => prev + winAmount);
            setWalletAmt(prev => prev + winAmount);
          }

          // Add floating text (only if there's something to celebrate).
          if (winAmount > 0) {
            floatingTextsRef.current.push({
              id: Math.random().toString(),
              text: `+₹${winAmount.toFixed(0)}`,
              x: endCoords.x,
              y: endCoords.y - 5,
              createdAt: now
            });
          }

          // Update History
          setHistory(prev => [
            { id: Math.random().toString(), mult, isBigWin: mult >= 10 },
            ...prev
          ].slice(0, 6));

          // 20-second history auto-clear
          if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
          historyTimeoutRef.current = setTimeout(() => {
            setHistory([]);
          }, 20000);

          // Do NOT push to newBalls (it gets destroyed)
          continue; 
        }

        // Recalculate start/end for the next segment smoothly
        startCoords = getPinCoords(ball.row, ball.col, rows);
        endCoords = getPinCoords(ball.row + 1, ball.path[ball.row + 1], rows);
      }

      // Interpolate position
      const p = ball.progress;
      const lerpX = startCoords.x + (endCoords.x - startCoords.x) * p;
      const lerpY = startCoords.y + (endCoords.y - startCoords.y) * p;
      
      // Arc effect (only if dropping between pins)
      const isDroppingToBucket = (ball.row === rows - 1);
      const arc = isDroppingToBucket ? 0 : Math.sin(p * Math.PI) * 3;

      ball.x = lerpX;
      ball.y = lerpY - arc;
      newBalls.push(ball);
      
      if (dt > 0) stateChanged = true; // constantly updating pos
    }

    // 2. Cleanup Floating Texts
    const validTexts = floatingTextsRef.current.filter(t => now - t.createdAt < 1000);
    if (validTexts.length !== floatingTextsRef.current.length) {
      floatingTextsRef.current = validTexts;
      stateChanged = true;
    }

    // 3. Cleanup Active Pins/Buckets visual states
    const activeP = { ...activePinsRef.current };
    let pinsChanged = false;
    for (const k in activeP) {
      if (now - activeP[k] > 200) {
        delete activeP[k];
        pinsChanged = true;
      }
    }
    if (pinsChanged) {
      activePinsRef.current = activeP;
      stateChanged = true;
    }

    const activeB = { ...activeBucketsRef.current };
    let bucketsChanged = false;
    for (const k in activeB) {
      if (now - activeB[k] > 400) {
        delete activeB[k];
        bucketsChanged = true;
      }
    }
    if (bucketsChanged) {
      activeBucketsRef.current = activeB;
      stateChanged = true;
    }

    // 4. Sync refs to React State for rendering
    if (stateChanged) {
      ballsRef.current = newBalls;
      setBalls(newBalls);
      setActivePins({ ...activePinsRef.current });
      setActiveBuckets({ ...activeBucketsRef.current });
      setFloatingTexts([...floatingTextsRef.current]);
    }

    frameRef.current = requestAnimationFrame(animate);
  }, [rows, multipliers, getPinCoords]);

  useEffect(() => {
    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [animate]);

  // 2026-05-30 — BACKEND-AUTHORITATIVE. The slot, the path, and the
  // payout all come from the server (POST /api/me/plinko/play). The
  // client only animates what the server already decided — there is NO
  // client-side RNG, so the result can't be tampered with.
  //
  // The server returns `path: ('L'|'R')[]` length === rows. We convert
  // it into the column-index format the animation pipeline already
  // consumes (`p[0]` = spawn at col 1; each step keeps col on 'L' and
  // increments on 'R'), so the rest of Page.tsx — Ball rendering,
  // bucket-hit accounting, history bar, multiplier highlights — keeps
  // working untouched.
  // 2026-05-30 — when /play fails with a TERMINAL error (insufficient
  // funds, daily loss cap, disabled, etc.), we kill the auto-bet timer so
  // a held button doesn't spam the server with N errors per second.
  // Cooldown/rate-limit errors are NOT terminal — those just get
  // swallowed and the next timer tick may succeed.
  const stopAutoBet = useCallback(() => {
    if (autoBetTimerRef.current) {
      clearInterval(autoBetTimerRef.current);
      autoBetTimerRef.current = null;
    }
  }, []);

  const handlePlay = async () => {
    audioEngine.init(); // Must initialize on user gesture
    audioEngine.toggle(!isMuted);

    // Guard against overlapping requests. The server enforces a cooldown
    // (default 800ms) and the auto-bet timer fires every 210ms, so without
    // this guard we'd spam the server with N×rejections per second AND the
    // counter would tick up while almost no balls actually dropped (the
    // exact bug Vinod reported: "hundreds of clicks, 50 balls"). The
    // counter is now incremented ONLY after a successful response below,
    // so it always matches the number of balls actually in flight.
    if (playInFlightRef.current) return;
    if (walletAmt < betAmount) {
      setPlayError('Not enough balance.');
      stopAutoBet();
      return;
    }
    playInFlightRef.current = true;
    setPlayError(null);

    try {
      const res = await api.post<PlayResponse>('/me/plinko/play', {
        bet: betAmount,
        rowCount: rows,
        risk: risk.toLowerCase(), // server expects 'low'|'medium'|'high'
      });

      // We've successfully booked a drop with the server. Play the drop
      // sound here (not at the top of the function) so a rejected /play
      // doesn't make a noise the user can't see a ball for.
      audioEngine.playDrop();

      // Convert server L/R path to FE column-index path.
      // p[0] = spawn (col 1), p[1] = first peg (col 1), then each
      // server step either stays (L) or increments by 1 (R).
      let c = 1;
      const p: number[] = [c, c];
      for (const step of res.path) {
        if (step === 'R') c += 1;
        p.push(c);
      }

      const newBall: Ball = {
        id: String(res.sessionId),
        row: -1,
        col: 1,
        path: p,
        progress: 0,
        x: 50,
        y: 0,
        value: betAmount,
        // Stamp the server-authoritative result on the ball. These are
        // applied to wallet + Session Winnings inside the animation loop
        // at the moment the ball lands — see the bucket-hit block above.
        payout: res.payout,
        multiplier: res.multiplier,
      };

      // ── INSTANT UI EFFECTS (bet portion only) ───────────────────────
      // Show the bet leaving the wallet IMMEDIATELY so the user gets
      // tactile feedback that the click was registered. The WIN portion
      // is deliberately deferred until the ball physically lands — that's
      // the whole reason this animation exists. v1 set walletAmt to
      // res.newBalance here (which already included the win), which is
      // why users saw the credit twice: once on click, once on land.
      setTotalBetAmt(prev => prev + betAmount);
      setWalletAmt(prev => prev - betAmount);

      // ── COUNTER (real drops only) ───────────────────────────────────
      // Counter increments ONLY when a ball is actually queued. Rejected
      // calls (in-flight overlap, cooldown, insufficient funds) do NOT
      // tick the counter. So the user sees "you dropped 12 balls" when
      // they did exactly that — not "you tapped 100 times".
      setPlayClickCount(prev => prev + 1);
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = setTimeout(() => setPlayClickCount(0), 5000);

      ballsRef.current.push(newBall);
    } catch (e) {
      const msg = (e instanceof ApiError ? e.message : String(e)) || 'Could not play. Try again.';
      // Cooldown-style errors are transient — we silently swallow them so
      // a held button can keep trying. Real errors (no balance, daily cap,
      // disabled, etc.) we surface AND stop auto-bet so we don't spam.
      const isCooldown = /Too fast/i.test(msg);
      if (!isCooldown) {
        setPlayError(msg);
        stopAutoBet();
      }
    } finally {
      playInFlightRef.current = false;
    }
  };

  const getBucketColor = (mult: number, isActive: boolean) => {
    if (isActive) return 'bg-white text-black shadow-[0_0_30px_rgba(255,255,255,1)] z-20 scale-110';
    
    // Losses (< 1)
    if (mult < 1) return 'bg-[#2a1738] text-white/90 shadow-[0_2px_0_#1a0f23]';
    // Small Wins (1 to 3)
    if (mult <= 3) return 'bg-[#6b21a8] text-white shadow-[0_2px_0_#4c1d95]';
    // Medium Wins (4 to 10)
    if (mult <= 10) return 'bg-[#c026d3] text-white shadow-[0_2px_0_#a21caf,0_0_15px_rgba(192,38,211,0.4)]';
    // Huge Wins (> 10)
    return 'bg-[#db2777] text-white shadow-[0_2px_0_#be185d,0_0_20px_rgba(219,39,119,0.7)]';
  };

  const getBallColor = (value: number) => {
    switch (value) {
      case 5: return { c1: '#a7f3d0', c2: '#10b981', shadow: 'rgba(16,185,129,0.8)' }; // Emerald
      case 10: return { c1: '#bfdbfe', c2: '#3b82f6', shadow: 'rgba(59,130,246,0.8)' }; // Blue
      case 15: return { c1: '#e9d5ff', c2: '#a855f7', shadow: 'rgba(168,85,247,0.8)' }; // Purple
      case 20: return { c1: '#fbcfe8', c2: '#ec4899', shadow: 'rgba(236,72,153,0.8)' }; // Pink
      default: return { c1: '#fbcfe8', c2: '#ec4899', shadow: 'rgba(236,72,153,0.8)' };
    }
  };

  // Distinct chip colours keyed by POSITION (not amount), so any admin-set
  // preset list renders with its own colour instead of collapsing to one.
  const BET_CHIP_ACTIVE = [
    'bg-gradient-to-b from-emerald-400 to-emerald-600 text-white border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]',
    'bg-gradient-to-b from-blue-400 to-blue-600 text-white border-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.3)]',
    'bg-gradient-to-b from-purple-400 to-purple-600 text-white border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)]',
    'bg-gradient-to-b from-pink-500 to-fuchsia-600 text-white border-fuchsia-400 shadow-[0_0_15px_rgba(219,39,119,0.3)]',
    'bg-gradient-to-b from-amber-400 to-orange-600 text-white border-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.3)]',
    'bg-gradient-to-b from-rose-400 to-red-600 text-white border-rose-400 shadow-[0_0_15px_rgba(244,63,94,0.3)]',
  ];
  const getBetButtonClass = (idx: number, isActive: boolean) =>
    !isActive
      ? 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white hover:border-white/20'
      : BET_CHIP_ACTIVE[((idx % BET_CHIP_ACTIVE.length) + BET_CHIP_ACTIVE.length) % BET_CHIP_ACTIVE.length];

  const renderBoard = () => {
    const dx = 100 / (rows + 2);
    
    // PINS
    const pins = [];
    for (let i = 0; i < rows; i++) {
      const pegsInRow = i + 3;
      for (let j = 0; j < pegsInRow; j++) {
        const coords = getPinCoords(i, j, rows);
        const pinId = `${i}-${j}`;
        const isActive = !!activePins[pinId];
        
        const isIdle = !isActive && balls.length === 0;
        const waveDelay = (i + j) * 0.05;

        pins.push(
          <circle
            key={pinId}
            cx={`${coords.x}%`}
            cy={`${coords.y}%`}
            r="0.8%"
            className={`transition-colors duration-100 ${isIdle ? 'animate-pin-idle-wave' : ''}`}
            style={{ 
              fill: isActive ? '#ffffff' : (isIdle ? undefined : 'rgba(255,255,255,0.15)'),
              filter: isActive ? 'drop-shadow(0px 0px 8px rgba(255,255,255,0.9))' : (isIdle ? undefined : 'none'),
              transform: isActive ? 'scale(1.3)' : (isIdle ? undefined : 'scale(1)'),
              transformOrigin: `${coords.x}% ${coords.y}%`,
              animationDelay: isIdle ? `${waveDelay}s` : undefined
            }}
          />
        );
      }
    }

    // BUCKETS
    const bucketElements = multipliers.map((m, j) => {
      // getPinCoords uses c as the path index. Bucket index = c - 1. 
      // So to render bucket j, we pass c = j + 1.
      const coords = getPinCoords(rows, j + 1, rows);
      const isActive = !!activeBuckets[j];
      const colorClass = getBucketColor(m, isActive);
      const width = dx * 0.85;
      const hits = bucketHits[j];
      const isIdle = !isActive && balls.length === 0;
      const waveDelay = Math.abs(j - rows / 2) * 0.1;

      return (
        <div
          key={`bucket-${j}`}
          className={`absolute flex flex-col items-center justify-center rounded-sm transition-all duration-100 ${colorClass} ${isIdle ? 'animate-bucket-pulse' : ''}`}
          style={{
            left: `${coords.x}%`,
            top: '86%',
            width: `${width}%`,
            height: '11%',
            transform: `translateX(-50%) ${isActive ? 'scale(0.85) translateY(10%)' : 'scale(1) translateY(0)'}`,
            animationDelay: isIdle ? `${waveDelay}s` : undefined
          }}
        >
          <span className="text-[9px] sm:text-[11px] md:text-[13px] font-black -rotate-90 origin-center tracking-tighter whitespace-nowrap leading-none">{m}x</span>
          {hits > 0 && !isActive && (
            <span className="absolute -bottom-5 text-[9px] font-bold text-white/50">{hits}</span>
          )}
        </div>
      );
    });

    // BALLS
    const ballElements = balls.map(b => {
      const colors = getBallColor(b.value);
      return (
        <div 
          key={b.id}
          className="absolute rounded-full z-20"
          style={{
            left: `${b.x}%`,
            top: `${b.y}%`,
            width: '3.5%',  // Big balls as requested!
            height: 'auto',
            aspectRatio: '1/1',
            transform: 'translate(-50%, -50%)',
            boxShadow: `0 0 15px ${colors.shadow}, inset 0 0 5px rgba(255,255,255,0.8)`,
            background: `radial-gradient(circle at 30% 30%, ${colors.c1}, ${colors.c2})`
          }}
        />
      );
    });

    // FLOATING TEXTS
    const textElements = floatingTexts.map(t => {
      const life = (Date.now() - t.createdAt) / 1000; // 0 to 1
      const floatY = t.y - life * 15; // floats up
      const opacity = 1 - life; // fades out

      return (
        <div
          key={t.id}
          className="absolute text-sm font-black text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.8)] z-40 pointer-events-none"
          style={{
            left: `${t.x}%`,
            top: `${floatY}%`,
            transform: 'translate(-50%, -50%)',
            opacity,
          }}
        >
          {t.text}
        </div>
      );
    });

    return (
      <div className="absolute inset-2 sm:inset-8 flex items-center justify-center pointer-events-none">
        <div className="relative w-full h-full max-w-full max-h-full aspect-square">
          {/* Put balls and buckets behind pins */}
          {bucketElements}
          {ballElements}
          
          <svg className="absolute inset-0 w-full h-full overflow-visible z-30">
            {pins}
          </svg>
          
          {textElements}
        </div>
      </div>
    );
  };  // Hide Global App UI when Plinko is active
  useEffect(() => {
    document.body.classList.add('plinko-active');
    return () => document.body.classList.remove('plinko-active');
  }, []);

  return (
    <div className="ludo-root plinko-root fixed inset-0 z-[9999]">
      {/* Shared settings panel — same affordance every game offers (Ludo /
          SL / Chess / Carrom / Rummy / XO). For Plinko, only the Sound
          toggle is active: voice/mic don't apply (single-player casino
          game), so we pass voiceEnabled={false} which hides those rows
          entirely inside the component. The Exit row leaves the game and
          returns to the home screen. */}
      <GameSettingsMenu
        open={settingsOpen}
        soundOn={!isMuted}
        micMuted={true}
        listenMuted={true}
        voiceEnabled={false}
        onToggleSound={() => { handleToggleSound(); }}
        onToggleMic={() => { /* not applicable for Plinko */ }}
        onToggleListen={() => { /* not applicable for Plinko */ }}
        onExit={() => { setSettingsOpen(false); navigate('/'); }}
        onClose={() => setSettingsOpen(false)}
        exitLabel="Exit to Home"
        anchor="left"
      />

      <style>{`
        /* Hide Site Chrome */
        body.plinko-active > #root > div > nav,
        body.plinko-active [data-site-nav],
        body.plinko-active [data-bottom-nav] { display: none !important; }

        .plinko-root .app-container {
          background: #0a0510;
        }
        
        @keyframes popIn {
          0% { transform: scale(0.5); opacity: 0; }
          70% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes historyDropIn {
          0% { 
            opacity: 0; 
            transform: translateY(-30px) scale(0.6) rotateX(-45deg); 
            filter: brightness(2) blur(4px);
          }
          60% { 
            transform: translateY(8px) scale(1.1) rotateX(10deg); 
            filter: brightness(1.5) blur(0px);
          }
          100% { 
            opacity: 1; 
            transform: translateY(0) scale(1) rotateX(0deg); 
            filter: brightness(1) blur(0px);
          }
        }
        .animate-history-drop {
          animation: historyDropIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          transform-origin: top center;
        }

        @keyframes historyBigWinGlow {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(219,39,119, 0.8), inset 0 0 10px rgba(255,255,255,0.5); 
            filter: brightness(1.2) hue-rotate(0deg);
            transform: scale(1.1);
          }
          50% { 
            box-shadow: 0 0 35px rgba(255,0,255, 1), inset 0 0 20px rgba(255,255,255,0.8); 
            filter: brightness(1.5) hue-rotate(-20deg);
            transform: scale(1.15);
          }
        }
        .animate-history-big-win {
          animation: historyBigWinGlow 1.5s ease-in-out infinite !important;
          z-index: 30;
          border-color: #fbcfe8 !important; /* Pink 200 */
        }

        @keyframes shimmerSlide {
          0%, 40% { transform: translateX(-150%) skewX(-20deg); }
          100% { transform: translateX(350%) skewX(-20deg); }
        }
        .animate-shimmer-slide {
          animation: shimmerSlide 4s ease-in-out infinite;
        }

        @keyframes pinIdleWave {
          0%, 100% { fill: rgba(255,255,255,0.15); filter: drop-shadow(0 0 0 transparent); transform: scale(1); }
          50% { fill: rgba(236,72,153,0.8); filter: drop-shadow(0 0 8px rgba(236,72,153,1)); transform: scale(1.25); }
        }
        .animate-pin-idle-wave {
          animation: pinIdleWave 2s ease-in-out infinite;
        }

        @keyframes bucketPulse {
          0%, 100% { filter: brightness(1) drop-shadow(0 0 0 transparent); }
          50% { filter: brightness(1.4) drop-shadow(0px -5px 12px rgba(236,72,153,0.5)); }
        }
        .animate-bucket-pulse {
          animation: bucketPulse 3s ease-in-out infinite;
        }

        @keyframes screenShake {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: translate(-4px, 3px) rotate(-1deg); }
          20%, 40%, 60%, 80% { transform: translate(4px, -3px) rotate(1deg); }
        }
        .animate-screen-shake {
          animation: screenShake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>

      <div className="app-container shadow-2xl text-white">
        {/* Background Ambient Glows */}
        <div className={`absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-fuchsia-600/20 blur-[120px] pointer-events-none transition-opacity duration-100 ${screenShake ? 'opacity-100' : 'opacity-40'}`} />
        <div className={`absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-pink-600/20 blur-[120px] pointer-events-none transition-opacity duration-100 ${screenShake ? 'opacity-100' : 'opacity-40'}`} />

        {/* Main Game Area */}
        <div className={`flex-1 flex flex-col pt-0 pb-2 relative z-10 w-full min-h-0 ${screenShake ? 'animate-screen-shake' : ''}`}>
          
          {/* Top Bar — STANDARDIZED LAYOUT (matches Ludo / Snake & Ladder /
              Chess / Carrom / Rummy / XO). The structure is identical to
              every other game: two circular buttons on the left (Back,
              Settings) and a wallet chip on the right with the same coin
              image + balance text + plus affordance. The ONLY difference
              is the colour palette — Plinko's purple→fuchsia gradient
              replaces Ludo's deep purple / SL's green / Chess's blue.

              The standalone sound-toggle button that used to live here as
              a third button has moved into the shared GameSettingsMenu
              (opened by the Settings button) so the layout matches the
              two-button convention every other game uses. */}
          <div
            className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#1a0b2e] to-[#3d1052] shadow-md z-[150] w-full border-b border-fuchsia-500/20 mb-4 shrink-0"
            style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
          >
            {/* Left cluster — Back + Settings (gap-2). Identical button
                dimensions and shapes to the other games. */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate(-1)}
                aria-label="Back"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
              >
                <Icon name="arrow_back" size={22} filled />
              </button>
              <button
                onClick={() => { audioEngine.init(); setSettingsOpen((o) => !o); }}
                aria-label="Settings"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
              >
                <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
              </button>
            </div>

            {/* Wallet chip — same coin image + balance + plus pattern as
                every other game. Gradient matches the top bar (purple ←
                fuchsia accent) so the chip blends into the bar. */}
            <div className="bg-gradient-to-b from-[#2d0d3f] to-[#1a0b2e] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-fuchsia-500/20 shadow-[0_0_30px_rgba(219,39,119,0.08)] relative">
              <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
              <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide">
                {fmt(walletAmt)}
              </span>
              <button
                type="button"
                aria-label="Recharge wallet"
                onClick={() => navigate('/wallet')}
                className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
              >
                <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
              </button>
            </div>
          </div>

          {/* Session Stats Bar */}
          <div className="flex-shrink-0 px-4 flex justify-center gap-3 sm:gap-6 z-20 relative mt-2 mb-2">
            
            {/* Session Bets */}
            <div className="bg-[#1a0f23] rounded-xl px-4 sm:px-6 py-2 flex flex-col items-center justify-center transition-all duration-300 border border-fuchsia-500/20 shadow-[0_0_30px_rgba(217,70,239,0.1)]">
              <span className="text-[9px] sm:text-[10px] font-black text-white/40 tracking-[0.2em] uppercase">Session Bets</span>
              <div className="text-lg sm:text-xl font-black flex items-center gap-1 text-white">
                <span className="text-fuchsia-500">₹</span>
                {totalBetAmt.toFixed(0)}
              </div>
            </div>

            {/* Session Winnings */}
            <div className={`bg-[#1a0f23] rounded-xl px-4 sm:px-6 py-2 flex flex-col items-center justify-center transition-all duration-300
              ${animateWinnings 
                ? 'border border-green-400 shadow-[0_0_20px_rgba(74,222,128,0.3)] scale-105' 
                : 'border border-fuchsia-500/20 shadow-[0_0_30px_rgba(217,70,239,0.1)]'
              }`}
            >
              <span className="text-[9px] sm:text-[10px] font-black text-white/40 tracking-[0.2em] uppercase">Session Winnings</span>
              <div className={`text-lg sm:text-xl font-black flex items-center gap-1 transition-colors duration-300 ${animateWinnings ? 'text-green-400' : 'text-white'}`}>
                <span className={animateWinnings ? 'text-green-400' : 'text-fuchsia-500'}>₹</span>
                {totalWonAmt.toFixed(2)}
              </div>
            </div>
            
          </div>

          {/* Physics Canvas Area */}
          <div className="flex-1 min-h-0 relative flex items-center justify-center px-2">
            <div className="w-full h-full relative flex items-center justify-center shrink-0">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-[#150a21] to-[#0c0414] border border-fuchsia-500/20 shadow-[0_0_50px_rgba(217,70,239,0.1)] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.02]" 
                  style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '40px 40px',
                    backgroundPosition: 'center center'
                  }}
                />
                
                {/* Vertical Floating History Indicator */}
                <div 
                  className="absolute right-1 sm:right-3 top-1 sm:top-3 flex flex-col w-10 sm:w-14 bg-[#0a0510]/60 backdrop-blur-xl rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] z-20 p-0.5 sm:p-1 gap-0.5 sm:gap-1"
                  style={{ perspective: '400px' }}
                >
                  {history.map((item, idx) => {
                    let bgColor = 'bg-[#2a1738]/80 text-white/70 border-white/5';
                    if (item.mult >= 2) bgColor = 'bg-gradient-to-br from-[#6b21a8] to-[#4c1d95] text-white border-purple-400/30';
                    if (item.mult >= 10) bgColor = 'bg-gradient-to-br from-[#c026d3] to-[#86198f] text-white border-fuchsia-400/50 shadow-[0_0_15px_rgba(192,38,211,0.5)]';
                    if (item.mult >= 50) bgColor = 'bg-gradient-to-br from-[#db2777] to-[#be185d] text-white border-pink-400/80 shadow-[inset_0_0_15px_rgba(255,255,255,0.4)]';

                    return (
                      <div 
                        key={item.id} 
                        className={`w-full py-1.5 sm:py-3 flex items-center justify-center text-[9px] sm:text-xs font-black transition-all duration-500 rounded-lg sm:rounded-xl border
                          ${idx === 0 ? 'opacity-100 animate-history-drop' : 'opacity-60 saturate-50'}
                          ${idx === 0 && item.isBigWin ? 'animate-history-big-win relative' : ''}
                          ${bgColor}
                        `}
                      >
                        {item.mult}x
                      </div>
                    );
                  })}
                </div>

                {renderBoard()}
              </div>
            </div>
          </div>
        </div>

        {/* Control Bottom Bar */}
        <div className="w-full flex-shrink-0 bg-[#12071a] border-t border-fuchsia-500/20 p-3 pb-6 sm:pb-4 flex flex-col gap-2 z-20 relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-fuchsia-600" />

          <div className="space-y-3">
            {/* Bet Amount Control */}
            <div className="space-y-1.5">
              <div className="flex items-center text-[10px] font-black text-white/50 uppercase tracking-widest gap-1.5">
                <Coins className="w-3 h-3 text-pink-500" /> Each Ball Value
              </div>
              
              <div className="grid gap-2"
                   style={{ gridTemplateColumns: `repeat(${Math.max(1, betPresets.length)}, minmax(0,1fr))` }}>
                {betPresets.map((val, i) => (
                  <button
                    key={val}
                    onClick={() => setBetAmount(val)}
                    className={`py-2 rounded-xl font-black text-sm border transition-all duration-200 transform active:scale-95
                      ${getBetButtonClass(i, betAmount === val)}
                    `}
                  >
                    ₹{val}
                  </button>
                ))}
              </div>
            </div>

            {/* Risk Level */}
            <div className="space-y-1.5">
              <div className="flex items-center text-[10px] font-black text-white/50 uppercase tracking-widest gap-1.5">
                <TrendingUp className="w-3 h-3 text-fuchsia-500" /> Risk Level
              </div>
              <div className="flex gap-2">
                {(['Low', 'Medium', 'High'] as const).map(r => (
                  <button
                    key={r}
                    onClick={() => setRisk(r)}
                    disabled={balls.length > 0}
                    className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 border
                      ${balls.length > 0 ? 'opacity-50 cursor-not-allowed grayscale' : ''}
                      ${risk === r 
                        ? 'bg-gradient-to-r from-pink-500/20 to-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50 shadow-[0_0_10px_rgba(217,70,239,0.1)]' 
                        : 'border-fuchsia-500/10 bg-white/5 text-white/40 hover:text-white/70 hover:bg-white/10'
                      }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Rows */}
            <div className="space-y-1.5">
              <div className="flex items-center text-[10px] font-black text-white/50 uppercase tracking-widest gap-1.5">
                <Layers className="w-3 h-3 text-pink-500" /> Rows
              </div>
              <div className="flex gap-2">
                {[8, 12, 16].map(r => (
                  <button
                    key={r}
                    onClick={() => setRows(r)}
                    disabled={balls.length > 0}
                    className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 border
                      ${balls.length > 0 ? 'opacity-50 cursor-not-allowed grayscale' : ''}
                      ${rows === r 
                        ? 'bg-gradient-to-r from-pink-500/20 to-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50 shadow-[0_0_10px_rgba(217,70,239,0.1)]' 
                        : 'border-fuchsia-500/10 bg-white/5 text-white/40 hover:text-white/70 hover:bg-white/10'
                      }`}
                  >
                    {r} Rows
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Bottom Controls */}
          <div className="mt-1 relative z-20">
            {/* Play Button */}
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                handlePlay();
                if (autoBetTimerRef.current) clearInterval(autoBetTimerRef.current);
                autoBetTimerRef.current = window.setInterval(() => {
                  handlePlay();
                }, 210); // 210ms - the sweet spot!
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                if (autoBetTimerRef.current) {
                  clearInterval(autoBetTimerRef.current);
                  autoBetTimerRef.current = null;
                }
              }}
              onPointerLeave={() => {
                if (autoBetTimerRef.current) {
                  clearInterval(autoBetTimerRef.current);
                  autoBetTimerRef.current = null;
                }
              }}
              onPointerCancel={() => {
                if (autoBetTimerRef.current) {
                  clearInterval(autoBetTimerRef.current);
                  autoBetTimerRef.current = null;
                }
              }}
              onContextMenu={(e) => e.preventDefault()} // prevent context menu on long press
              style={{ touchAction: 'none' }} // prevent scrolling while holding button
              className="w-full py-4 bg-gradient-to-t from-fuchsia-600 to-pink-500 hover:from-fuchsia-500 hover:to-pink-400 text-white rounded-2xl font-black text-2xl sm:text-3xl tracking-widest shadow-[0_0_40px_rgba(219,39,119,0.4)] active:scale-95 transition-all flex items-center justify-center border border-pink-400/50 relative overflow-visible group select-none"
            >
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                <div className="absolute top-0 bottom-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 animate-shimmer-slide" />
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="relative z-10 drop-shadow-md">PLAY</span>
              
              {/* Continuous Click Combo Indicator */}
              {playClickCount > 0 && (
                <div 
                  key={playClickCount} // forces animation re-trigger on every click
                  className="absolute -top-4 -right-2 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-black border-2 border-yellow-200 shadow-[0_0_20px_rgba(250,204,21,0.6)] animate-[popIn_0.2s_ease-out_forwards] z-30"
                >
                  {playClickCount}
                </div>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
